import React, {
  useState,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  ChangeEvent,
  KeyboardEvent,
  FormEvent,
} from "react";
import styles from "../../components/live-support/liveSupportComponent.module.scss";
import Pusher from "pusher-js";
import { getCookie } from "../../api/utils/utils";
import SupportServices from "./services/SupportService";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";

interface Message {
  id: number;
  message: string;
  isUser: boolean;
  is_file: number;
  timestamp: string;
  date?: string;
  isParent?: boolean;
  templateId?: number;
  hasChildren?: boolean;
  isContent?: boolean;
  isLink?: boolean;
}

interface Props {
  setMessages: Dispatch<SetStateAction<Message[]>>;
  chatId?: string | number;
}

const LiveChatSection = ({ setMessages, chatId }: Props) => {
  const [isDropDown, setIsDropDown] = useState(false);
  const [inputMessage, setInputMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const activeChatId =
    chatId ||
    (typeof window !== "undefined"
      ? sessionStorage.getItem("guest-chat-id")
      : null) ||
    getCookie("guest-chat-id");

  const translations = useSelector(
    (state: RootState) => state.translations?.data
  );

  const dropDownRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imgInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropDownRef.current &&
        !dropDownRef.current.contains(event.target as Node)
      ) {
        setIsDropDown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!activeChatId) return;

    const pusher = new Pusher("66fa74a20735682f38a2", {
      cluster: "ap2",
      forceTLS: true,
      authEndpoint: "/pusher/auth",
    });

    const channel = pusher.subscribe("chat-channel");
    const eventName = `user-${activeChatId}`;

    const handleIncomingMessage = (data: any) => {
      const parsedData = typeof data === "string" ? JSON.parse(data) : data;
      const userMessage: Message = {
        id: Number(parsedData?.id) || Date.now(),
        message: parsedData?.content || parsedData?.message || "",
        is_file: Number(parsedData?.is_file) || 0,
        isUser: parsedData?.sender_type === "user",
        timestamp:
          parsedData?.hour ||
          new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
        date: parsedData?.date,
      };

      setMessages((prev) => {
        // Avoid duplicate message if already added
        const exists = prev.some(
          (m) =>
            m.id === userMessage.id &&
            m.message === userMessage.message &&
            m.isUser === userMessage.isUser
        );
        if (exists) return prev;
        return [...prev, userMessage];
      });
    };

    channel.bind(eventName, handleIncomingMessage);

    return () => {
      channel.unbind(eventName, handleIncomingMessage);
      channel.unsubscribe();
      pusher.disconnect();
    };
  }, [activeChatId, setMessages]);

  const handleSendMessage = async (
    isFile: number,
    messageContent: string | File
  ) => {
    if (!activeChatId) return;
    if (!messageContent) return;
    if (!isFile && typeof messageContent === "string" && messageContent.trim() === "") {
      return;
    }

    let payload: any;
    if (isFile) {
      payload = new FormData();
      payload.append("is_file", "1");
      payload.append("content", messageContent as File);
    } else {
      payload = { is_file: 0, content: (messageContent as string).trim() };
    }

    setIsSending(true);
    const textBackup = !isFile ? inputMessage : "";
    if (!isFile) {
      setInputMessage("");
    }

    try {
      const res = await SupportServices.sendMessage(Number(activeChatId), payload);
      if (!isFile && typeof messageContent === "string") {
        const optimisticMsg: Message = {
          id: res?.data?.id || res?.data?.message?.id || Date.now(),
          message: messageContent.trim(),
          is_file: 0,
          isUser: true,
          timestamp: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
        };
        setMessages((prev) => {
          const exists = prev.some(
            (m) =>
              m.id === optimisticMsg.id ||
              (m.message === optimisticMsg.message && m.isUser && m.timestamp === optimisticMsg.timestamp)
          );
          if (exists) return prev;
          return [...prev, optimisticMsg];
        });
      }
    } catch (err) {
      console.error("Failed to send message:", err);
      if (!isFile) {
        setInputMessage(textBackup);
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(0, inputMessage);
    }
  };

  const handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSendMessage(0, inputMessage);
  };

  const handleToggleDropDown = () => {
    setIsDropDown((prev) => !prev);
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsDropDown(false);
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      handleSendMessage(1, file);
      e.target.value = "";
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsDropDown(false);
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      handleSendMessage(1, file);
      e.target.value = "";
    }
  };

  const isMessageEmpty = !inputMessage || inputMessage.trim().length === 0;

  return (
    <div className={styles.inputAreaWrapper}>
      {isDropDown && (
        <div ref={dropDownRef} className={styles.attachmentPopup}>
          <button
            type="button"
            className={styles.attachmentOption}
            onClick={() => imgInputRef.current?.click()}
            tabIndex={0}
            aria-label="Şəkil seçin"
          >
            <div className={styles.attachmentIconBox}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
            </div>
            <span>{translations?.send_image || "Şəkil göndər"}</span>
          </button>

          <button
            type="button"
            className={styles.attachmentOption}
            onClick={() => fileInputRef.current?.click()}
            tabIndex={0}
            aria-label="Fayl seçin"
          >
            <div className={styles.attachmentIconBox}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
            </div>
            <span>{translations?.send_file || "Fayl göndər"}</span>
          </button>

          <input
            ref={imgInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleImageChange}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.txt,.zip,.rar,.json"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </div>
      )}

      <form className={styles.chatInputForm} onSubmit={handleFormSubmit}>
        <button
          type="button"
          onClick={handleToggleDropDown}
          className={`${styles.attachBtn} ${isDropDown ? styles.attachBtnActive : ""}`}
          aria-label="Fayl və ya şəkil əlavə et"
          tabIndex={0}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
          </svg>
        </button>

        <textarea
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          placeholder={translations?.write_message || "Mesajınızı yazın..."}
          onKeyDown={handleKeyDown}
          rows={1}
          className={styles.modernChatTextarea}
          aria-label="Mesaj yazın"
        />

        <button
          className={`${styles.modernSendBtn} ${
            !isMessageEmpty && !isSending ? styles.modernSendBtnActive : ""
          }`}
          type="submit"
          disabled={isMessageEmpty || isSending}
          aria-label="Göndər"
          tabIndex={0}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </form>
    </div>
  );
};

export default LiveChatSection;
